import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError, registerAuthDriftHandler } from "../lib/api";
import { toast } from "sonner";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = signed-out, object = signed-in
  const [bootstrapped, setBootstrapped] = useState(false);
  const userRef = useRef(null);           // mirror of `user` for interceptor comparisons
  const driftHandledRef = useRef(false);  // guard against toast+reload storms

  const handleDrift = useCallback((newUser) => {
    if (driftHandledRef.current) return;
    driftHandledRef.current = true;
    const prevName = userRef.current?.name || "someone";
    const nextName = newUser?.name || "another account";
    toast.error(
      `Signed-in user changed from ${prevName} to ${nextName}. Reloading…`,
      { duration: 3000 }
    );
    setTimeout(() => window.location.reload(), 900);
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      const prev = userRef.current;
      // If we already had a user in memory AND the cookie now belongs to a different user id,
      // the FE state is stale (usually caused by another sign-in in the same browser profile).
      if (prev && prev.id && data?.id && prev.id !== data.id) {
        handleDrift(data);
        return data;
      }
      userRef.current = data;
      setUser(data);
      return data;
    } catch (e) {
      // Only clear the session on a true auth failure (401). Network blips, 5xx, and CORS errors
      // must NOT bounce the user to login — they'd just retry the next click.
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        userRef.current = null;
        setUser(false);
      }
      return null;
    }
  }, [handleDrift]);

  // Boot
  useEffect(() => {
    (async () => {
      await refreshMe();
      setBootstrapped(true);
    })();
  }, [refreshMe]);

  // Re-sync whenever the tab regains focus (catches another tab logging in as a different user)
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" && userRef.current?.id) {
        refreshMe();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, [refreshMe]);

  // Whenever the api layer sees a 401/403/409 that suggests role/ownership mismatch, ask us to re-sync.
  useEffect(() => {
    registerAuthDriftHandler(() => {
      if (userRef.current?.id) refreshMe();
    });
    return () => registerAuthDriftHandler(null);
  }, [refreshMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      driftHandledRef.current = false;
      userRef.current = data;
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch { /* server may be unreachable; still clear local state */ }
    driftHandledRef.current = false;
    userRef.current = null;
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, bootstrapped, login, logout, refreshMe }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
